import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useMatch } from 'react-router';
import { useGetCountriesListQuery } from 'services/assets';
import { useGetBridgeKycInfoQuery, useGetMerchantQuery, useUpdateMerchantKybMutation } from 'services/merchants';
import { TitanKybDataResponse } from 'services/merchants/types';

import { SelectItem } from 'components/inputs/select/select';
import LoadingIndicator from 'components/loadingIndicator/loadingIndicator';

import { ModalStateProps } from 'pages/merchant/route';

import selectAccount from 'state/selectors/accounts/accountSelector';

import { FormType, KEYS } from './keys';
import { KYB } from './kyb';
import { useBackendErrors } from './useBackendErrors';
import { useSessionForm } from './useSessionForm';
import { getInitialFormValues } from './utils/getInitialFormValues';
import mapBackendErrorsToFormFields from './utils/mapBackendErrorsToFormFields';

interface AutoKybFlowProps {
  run: () => Promise<void>;
  isProcessing: boolean;
  titanData: TitanKybDataResponse | undefined;
  isLoadingTitanData: boolean;
  fetchTitanData: () => Promise<void>;
}

interface KybSectionContainerProps extends ModalStateProps {
  autoKybFlowProps: AutoKybFlowProps;
  shouldShowPanel: boolean;
  openAssociatedPersonModal?: () => void;
}

//  todo: Ensure it doesn't get added to the render tree at all unless the user is an admin or superadmin.
export const KybSectionContainer = ({
  visibleModal,
  setVisibleModal,
  autoKybFlowProps,
  shouldShowPanel,
  openAssociatedPersonModal,
}: KybSectionContainerProps) => {
  const matchId = useMatch(KEYS.ROUTE_MATCH_ID);
  const match = useMatch(KEYS.ROUTE_MATCH);
  const selectedMatch = matchId || match;
  const { account } = useSelector(selectAccount);
  const { uuid } = selectedMatch?.params ?? { uuid: account.uuid };
  const { data: merchantResponse, isLoading } = useGetMerchantQuery(uuid!, { skip: !uuid });
  const { data: bridgeKycInfo } = useGetBridgeKycInfoQuery(uuid!, { skip: !uuid });
  const { data: countries = [] } = useGetCountriesListQuery();
  const [updateMerchantKyb, { data: merchantKybData, isLoading: isUpdatingMerchantKyb, error: merchantKybError, reset: resetMutation }] =
    useUpdateMerchantKybMutation();

  const { backendErrors, setMappedErrors, clearBackendErrors } = useBackendErrors();

  const { run, isProcessing, titanData, isLoadingTitanData, fetchTitanData } = autoKybFlowProps;

  const initialFormValues = useMemo(
    () => getInitialFormValues(merchantResponse?.data?.name, merchantResponse?.data?.uuid),
    [merchantResponse],
  );

  const titanFormData = titanData?.data?.merchant;
  const sessionControllers = useSessionForm(KEYS.STORAGE_KEY, initialFormValues);

  useEffect(() => {
    if (merchantKybError) {
      setMappedErrors(merchantKybError, mapBackendErrorsToFormFields);
    } else if (merchantKybData?.data?.success) {
      clearBackendErrors();
    }
  }, [merchantKybError, merchantKybData]);

  const handleUpdateKyb = async (merchantKybPayload: Partial<FormType>) => {
    if (uuid) {
      await updateMerchantKyb({ uuid, ...merchantKybPayload });
    }
  };

  const mappedCountries: SelectItem[] = countries.map((country) => ({
    key: country.isoAlpha3,
    icon: country.iconUrl,
    label: country.name,
  }));

  if (isLoading) {
    return <LoadingIndicator padded />;
  }

  if (!merchantResponse?.data) {
    return null;
  }

  return (
    <KYB
      countries={mappedCountries}
      merchant={merchantResponse.data}
      onConfirm={handleUpdateKyb}
      error={backendErrors}
      isUpdatingMerchantKyb={isUpdatingMerchantKyb}
      merchantKybData={merchantKybData}
      clearBackendErrors={clearBackendErrors}
      resetMutation={resetMutation}
      autoKybProps={{
        run,
        isProcessing,
      }}
      titanFormData={titanFormData}
      isLoadingTitanData={isLoadingTitanData}
      fetchTitanData={fetchTitanData}
      bridgeKycInfo={bridgeKycInfo}
      openAssociatedPersonModal={openAssociatedPersonModal}
      sessionControllers={sessionControllers}
      initialFormValues={initialFormValues}
      visibleModal={visibleModal}
      setVisibleModal={setVisibleModal}
      shouldShowPanel={shouldShowPanel}
    />
  );
};
